import { NavLink as RouterLink } from 'react-router-dom';
// @mui
import { Box, Link } from '@mui/material';
// type
import type { NavItemProps } from '../type';
//
import { Iconify } from '../../Iconify';
import {
  ListItemIconStyle,
  ListItemStyle,
  ListItemTextStyle,
  ListSubItemTextStyle,
} from '../style';
import { isExternalLink } from '..';
import type { JSX } from 'react';
// ----------------------------------------------------------------------

export function NavItemRoot({
  item,
  isCollapse,
  open = false,
  active,
  onOpen,
}: NavItemProps): JSX.Element {
  const { title, path, icon, info, children, useDotWhenActive } = item;

  const renderContent = (
    <>
      {icon && <ListItemIconStyle active={active}>{icon}</ListItemIconStyle>}
      {!icon && useDotWhenActive && <DotIcon active={!!active} />}
      <ListItemTextStyle
        data-cy="navigation-menu-item"
        disableTypography
        primary={title}
        isCollapse={isCollapse}
        active={active}
      />
      {!isCollapse && (
        <>
          {info && info}
          {children && <ArrowIcon open={open} />}
        </>
      )}
    </>
  );

  if (children) {
    return (
      <ListItemStyle onClick={onOpen} activeRoot={active}>
        {renderContent}
      </ListItemStyle>
    );
  }

  return isExternalLink(path) ? (
    <ListItemStyle component={Link} href={path} target="_blank" rel="noopener">
      {renderContent}
    </ListItemStyle>
  ) : (
    <ListItemStyle
      component={RouterLink}
      to={path}
      activeRoot={useDotWhenActive ? false : active}
    >
      {renderContent}
    </ListItemStyle>
  );
}

// ----------------------------------------------------------------------

type NavItemSubProps = Omit<NavItemProps, 'isCollapse'>;

export function NavItemSub({
  item,
  open = false,
  active = false,
  onOpen,
}: NavItemSubProps): JSX.Element {
  const { title, path, info, children } = item;

  const renderContent = (
    <>
      <DotIcon active={active} />
      <ListSubItemTextStyle disableTypography primary={title} active={active} />
      {info && info}
      {children ? <ArrowIcon open={open} /> : <Box sx={{ width: 25 }} />}
    </>
  );

  if (children) {
    return (
      <ListItemStyle onClick={onOpen} activeSub={active} subItem>
        {renderContent}
      </ListItemStyle>
    );
  }

  return isExternalLink(path) ? (
    <ListItemStyle
      component={Link}
      href={path}
      target="_blank"
      rel="noopener"
      subItem
    >
      {renderContent}
    </ListItemStyle>
  ) : (
    <ListItemStyle component={RouterLink} to={path} activeSub={active} subItem>
      {renderContent}
    </ListItemStyle>
  );
}

// ----------------------------------------------------------------------

interface DotIconProps {
  active: boolean;
}

export function DotIcon({ active }: DotIconProps): JSX.Element {
  return (
    <ListItemIconStyle>
      <Box
        component="span"
        sx={{
          width: 4,
          height: 4,
          borderRadius: '50%',
          bgcolor: 'text.disabled',
          transition: (theme) =>
            theme.transitions.create('transform', {
              duration: theme.transitions.duration.shorter,
            }),
          ...(active && {
            transform: 'scale(2)',
            bgcolor: 'primary.main',
          }),
        }}
      />
    </ListItemIconStyle>
  );
}

// ----------------------------------------------------------------------

interface ArrowIconProps {
  open: boolean;
}

export function ArrowIcon({ open }: ArrowIconProps): JSX.Element {
  return (
    <Iconify
      icon={open ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'}
      sx={{
        width: 16,
        height: 16,
        ml: 1,
      }}
    />
  );
}
